import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { View } from '@olxbr/design-system'
import { Button, LoadingButton } from '@olxbr/ds-core'
import { TextInput, Checkbox, withMargins } from '@olxbr/ds-web'
import Form from '../../atoms/Form'
import { OauthLoginCallout } from '../../molecules/OauthLoginCallout'
import PasswordWithStrengthMeter from '../../organisms/PasswordWithStrengthMeter'
import behaviors from '../../../util/behaviors'
import Lurker from '../../../util/lurker'
import { GenericErrorBox } from '../../molecules/GenericErrorBox'
import {
  isValidNickname,
  isValidEmail,
  maxNumbers,
  isValidateDate,
  isValidFullName,
} from '../../../util/validators'
import emailAlreadyExists from './emailExistMsg'
import AlternativeRuler from '../../molecules/AlternativeRuler'
import ErrorMsgCallout from '../../molecules/ErrorMsgCallout'
import MailCheckInput from '../../organisms/MailCheckInput'
import { registerWithCaptchaValidatingDocument } from '../../../api/registerApiCall'
import passwordlessSendEmailApiCall from '../../../api/passwordlessSendEmailApiCall'
import Recaptcha from '../../atoms/Recaptcha'
import ErrorMsg from '../../atoms/ErrorMsg'
import Tracker from '../../../util/tracker'
import Col from '../../atoms/Col'
import Row from '../../atoms/Row'
import { isStrongPasswordExperimentOn } from '../../../util/experiments'

import BaseForm from '../BaseForm'
import { DocumentInput } from '../../atoms/DocumentInput'
import { isValidDocument } from '../../../util/document'
import { AlertCircle } from '@olxbr/ds-icons'
import { ResponsiveTooltip } from '../../atoms/ResponsiveTooltip'
import StrongPasswordInput, { isValidPassword } from '../../molecules/StrongPasswordInput'
import { DocumentTypeSelection } from '../../molecules/DocumentTypeSelection'
import { PASSWORD_MAX_LENGTH } from '../../../util/password'

const lurker = new Lurker('signup')
const tracker = new Tracker('signup')

const Content = styled(View)`
  display: flex;
  flex-direction: column;
`

const PageForm = styled(Form)`
  margin-top: 8px;
`

const ErrorMsgWrapper = styled(View)`
  margin-top: 16px;
  margin-right: 0;
  margin-bottom: 16px;
  margin-left: 0;
`

const ButtonWrapper = styled(View)`
  margin-top: 16px;
`

const RecaptchaWrapper = styled(View)`
  margin-top: 24px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const CaptchaErrorMsgView = styled(View)`
  display: flex;
  width: 90%;
`

const RowWrapper = styled(Row)`
  margin-bottom: var(--spacing-3);
  ${withMargins}
`

const getPasswordExtraData = (key, password) => {
  const field = {}
  const passwordLevel = password.level

  if (passwordLevel === -1) {
    field[key] = 'sequential'
  } else if (passwordLevel === 1 || passwordLevel === 2) {
    field[key] = 'weak'
  } else if (passwordLevel === 3) {
    field[key] = 'good'
  } else if (passwordLevel === 4) {
    field[key] = 'strong'
  }
  return field
}

const TextCenter = styled.div`
  text-align: center;
`

const TextInputDate = styled(TextInput)`
  &::-webkit-calendar-picker-indicator {
    background-color: rgba(0, 0, 0, 0);
    cursor: pointer;
    width: 0;
    height: 0;
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
  }
`

const ErrorEmailBlacklisted = () => <ErrorMsgCallout text="Este e-mail não pode ser utilizado." />

const ErrorNicknameBlacklisted = () => (
  <ErrorMsgCallout text="Este apelido não pode ser utilizado." />
)

const defaultEmailErrorMsg = 'Por favor, insira um endereço de e-mail válido.'
const getErrorMessage = email => {
  if (email) {
    return defaultEmailErrorMsg
  }
  return 'Este campo é obrigatório'
}

const validValue = value =>
  typeof value === 'string' ? value.length >= 3 && maxNumbers(value, 5) : !!value

const getNickNameErrorMsg = value =>
  value.length < 3
    ? 'Por favor, preencha ao menos 3 caracteres'
    : 'Seu apelido não pode ter mais de 5 caracteres numéricos'

const maxRetriesReachedError = Error('REACHED_MAX_RETRIES')

const errors = {
  showGenericError: false,
  showInactiveError: false,
  showNicknameBlacklistError: false,
  showEmailBlacklistError: false,
  showEmailNotFoundError: false,
  showGoogleInitializationError: false,
  showPasswordlessError: false,
}

const recaptchaSiteKey = '6LfPClEUAAAAAD_O4ppsMM0r5EX5pcV5PH12rBIn'

class RegisterTemplate extends BaseForm {
  constructor(props) {
    super(props)
    this.state = {
      ...errors,
      abTests: this.props.abTests || '',
      isSubmitting: false,
      validators: {
        nickname: {
          action: value => isValidNickname(value),
          message: value => getNickNameErrorMsg(value),
        },
        legalDocument: {
          action: value => this.isValidDocument(value),
          message: this.getDocumentErrorMessage,
        },
        userType: {
          action: value => value,
          message: 'Campo obrigatório. Informe o tipo da sua conta.',
        },
        email: {
          action: isValidEmail,
          message: getErrorMessage,
        },
        password: {
          action: isStrongPasswordExperimentOn(this.props.abTests)
            ? isValidPassword
            : this.isValidPassword,
        },
        recaptchaToken: {
          action: value => value,
          message: 'Captcha não preenchido.',
        },
      },
      recaptchaReady: false,
      fields: {
        nickname: {
          value: '',
          pristine: true,
          valid: false,
        },
        userType: {
          value: 'cpf',
          pristine: true,
          valid: true,
        },
        legalDocument: {
          value: '',
          pristine: true,
          valid: false,
        },
        email: {
          value: '',
          pristine: true,
          valid: false,
        },
        password: {
          value: '',
          pristine: true,
          valid: false,
          level: 0,
        },
        recaptchaToken: {
          value: '',
          pristine: true,
          valid: false,
        },
      },
      isMEI: false,
      fullName: {
        value: '',
        pristine: true,
        valid: false,
      },
      birthDate: {
        value: '',
        pristine: true,
        valid: false,
      },
    }
  }

  componentDidMount() {
    lurker.pageLoadEvt()
    tracker.pageLoadEvt()
  }

  showError(error, extra) {
    this.setState({ ...errors, ...extra, [error]: true })
  }

  onNicknameChange = event => {
    const { value } = event.target
    const isValid = validValue(value)
    this.updateField('nickname', value, isValid, true)
  }

  onNicknameBlur = event => {
    const { value } = event.target
    const isValid = validValue(value)

    let errorMsg = ''

    if (!isValid) {
      errorMsg = getNickNameErrorMsg(value)
    }

    this.updateField('nickname', value, isValid, false, errorMsg)
  }

  onEmailChange = event => {
    const { value } = event.target
    this.updateField('email', value, null, true)
  }

  onEmailBlur = event => {
    const { value } = event.target
    const valid = isValidEmail(value)
    const message =
      value.length > 0 && !valid ? 'Por favor, insira um endereço de e-mail válido.' : null
    this.updateField('email', value, valid, false, message)
  }

  onPasswordChange = (value, level, msg) => {
    const fields = { ...this.state.fields }
    this.setState({
      fields: {
        ...fields,
        password: {
          value,
          valid: this.isValidPassword(value, level),
          level,
          msg,
          pristine: false,
        },
      },
    })
  }

  onPasswordChangeVariant = event => {
    const fields = { ...this.state.fields }
    this.setState({
      fields: {
        ...fields,
        password: {
          value: event.target.value,
          valid: isValidPassword(event.target.value),
          pristine: false,
        },
      },
    })
  }

  onUserTypeChange = event => {
    const fields = { ...this.state.fields }
    this.setState({
      fields: {
        ...fields,
        userType: {
          value: event.target.value,
          valid: event.target.value === 'cpf' || event.target.value === 'cnpj',
          pristine: false,
        },
        legalDocument: {
          ...fields.legalDocument,
          value: '',
          valid: true,
        },
      },
      isMEI: false,
    })

    const eventType = event.target.value === 'cpf' ? 'cpf_radio_button' : 'cnpj_radio_button'
    lurker.clickEvt(eventType)
  }

  onLegalDocumentBlur = event => {
    const { value } = event.target
    const { userType } = this.state.fields
    const { message } = this.state.validators.legalDocument
    const valid = isValidDocument(value, userType.value)
    const messageValue = value.length > 0 && !valid ? message() : null
    this.updateField('legalDocument', value, valid, false, messageValue)
  }

  onClickGoogleLoginContactLink = () => {
    // eslint-disable-line class-methods-use-this
    lurker.clickEvt('test_click_fale_conosco')
  }

  onOauthError = error => {
    if (this.props.onError) {
      this.props.onError(error)
    }
    let errorBox = 'showGenericError'
    if (error === 'google_initialization_error') {
      errorBox = 'showGoogleInitializationError'
    }
    this.showError(errorBox, { isSubmitting: false })
  }

  onCancel = () => {
    this.setState({ isSubmitting: false })
  }

  onOauthSuccess = data => {
    if (this.props.onRegisterSuccess) {
      this.props.onRegisterSuccess(data)
    }
    this.setState({ isSubmitting: false })
  }

  onOauthSubmit = () => {
    this.setState({ isSubmitting: true })
  }

  sendEmailSignUpEventToGA = userId => {
    if (window) {
      const { dataLayer } = window
      if (dataLayer) {
        dataLayer.push({ event: 'email_signup_success', user_id: userId })
      }
    }
  }

  doRegistration = async () => {
    const { fields, abTests, isMEI, fullName, birthDate } = this.state
    const { nickname, email, password, legalDocument, userType, recaptchaToken } = fields

    let registrationData = {
      nickname: nickname.value,
      email: email.value.toLowerCase().trim(),
      password: password.value,
      legalDocument: legalDocument.value.replace(/[^0-9]/g, ''),
      userType: userType.value,
    }

    if (isMEI) {
      registrationData = {
        ...registrationData,
        extraData: { isMei: isMEI },
      }
    }

    if (userType.value === 'cpf') {
      registrationData = {
        ...registrationData,
        fullName: fullName.value,
        extraData: { birthDate: birthDate.value },
      }
    }

    const { status, data } = await registerWithCaptchaValidatingDocument(
      registrationData,
      abTests,
      recaptchaToken.value
    )

    if (status === 202) {
      this.sendEmailSignUpEventToGA(data?.publicAccountId)
      lurker.viewMessageEvt('send_email_success', {
        ...getPasswordExtraData('password_strength', fields.password),
      })
      if (this.props.onSendEmailSuccess) {
        this.props.onSendEmailSuccess(email.value)
      }
    } else {
      const docType = userType.value.toUpperCase()
      this.sendEmailSignUpEventToGA(data?.publicAccountId)
      lurker.viewMessageEvt('email_signup_success', {
        remote_addr: data.remoteAddress,
        ...getPasswordExtraData('password_strength', fields.password),
        doc_type: docType,
        is_mei: isMEI,
      })
      tracker.viewMessageEvt('sign_up_email', {
        remote_addr: data.remoteAddress,
        ...getPasswordExtraData('password_strength', fields.password),
      })
      if (this.props.onRegisterSuccess) {
        this.props.onRegisterSuccess({ ...data, method: 'email' })
      }
    }
    this.setState({ isSubmitting: false })
  }

  invalidRecaptchaToken(error) {
    return error.data['error-codes'] && error.data['error-codes'][0] === 'invalid-input-response'
  }

  checkActiveErrors = errors => {
    let errorMessages = {
      cpfError: null,
      cnpjError: null,
      emailError: null,
    }

    const processError = error => {
      if (error.startsWith('CPF_')) {
        errorMessages.cpfError = error
      } else if (error.startsWith('CNPJ_')) {
        errorMessages.cnpjError = error
      } else if (error.startsWith('EMAIL_')) {
        errorMessages.emailError = error
      }
    }

    if (Array.isArray(errors)) {
      errors.forEach(processError)
    } else if (typeof errors === 'string') {
      processError(errors)
    }

    return errorMessages
  }

  doRegistrationStack = async () => {
    const { passwordlessSignIn } = this.props
    const { fields, isMEI } = this.state
    this.setState(errors)

    try {
      await this.doRegistration()
    } catch (data) {
      if (this.props.onError) {
        this.props.onError()
      }
      const { response = {} } = data

      if (response.status === 409) {
        lurker.viewMessageEvt('error', {
          accounts_reason: 'already_signed_user',
          accounts_status_code: response.status,
        })

        if (passwordlessSignIn) {
          passwordlessSendEmailApiCall(fields.email.value)
            .then(() => {
              fields.email.msg = 'Este e-mail já possui cadastro.'
              fields.email.valid = false
              this.showError('showPasswordlessError', {
                passwordlessEmail: fields.email.value,
                fields,
              })
            })
            .catch(() => this.showEmailAlreadyExistsError())
        } else {
          const errorMessages = this.checkActiveErrors(response.data.message)
          const docType = fields.userType.value.toUpperCase()

          if (errorMessages.cpfError) {
            lurker.viewMessageEvt('error', {
              accounts_reason: errorMessages.cpfError,
              accounts_status_code: response.status,
              doc_type: docType,
              is_mei: isMEI,
            })

            this.showDocumentAlreadyExistsError('Este CPF já possui cadastro.')
          } else if (errorMessages.cnpjError) {
            lurker.viewMessageEvt('error', {
              accounts_reason: errorMessages.cnpjError,
              accounts_status_code: response.status,
              doc_type: docType,
              is_mei: isMEI,
            })

            this.showDocumentAlreadyExistsError('Este CNPJ já possui cadastro.')
          }

          if (errorMessages.emailError) {
            lurker.viewMessageEvt('error', {
              accounts_reason: errorMessages.emailError,
              accounts_status_code: response.status,
            })

            this.showEmailAlreadyExistsError()
          }
        }
      } else if (response.status === 422) {
        lurker.viewMessageEvt('error', {
          accounts_reason: 'unprocessable_entity',
          accounts_status_code: response.status,
        })
        this.showError('showGenericError')
      } else if (response.status === 410) {
        lurker.viewMessageEvt('error', {
          accounts_reason: 'account_inactive',
          accounts_status_code: response.status,
        })
        this.showError('showInactiveError')
      } else if (response.status === 403) {
        if (response.data && response.data.message) {
          if (response.data.message === 'NICKNAME_IN_BLACKLIST') {
            lurker.viewMessageEvt('error', {
              accounts_reason: 'nickname_in_blacklist',
              accounts_status_code: response.status,
            })
            fields.nickname.msg = ErrorNicknameBlacklisted
            fields.nickname.valid = false
            this.showError('showNicknameBlacklistError', { fields })
          } else if (response.data.message === 'EMAIL_IN_BLACKLIST') {
            lurker.viewMessageEvt('error', {
              accounts_reason: 'email_in_blacklist',
              accounts_status_code: response.status,
            })
            fields.email.msg = ErrorEmailBlacklisted
            fields.email.valid = false
            this.showError('showEmailBlacklistError', { fields })
          } else if (response.data.message === 'BAD_EMAIL_RISK') {
            lurker.viewMessageEvt('error', {
              accounts_reason: 'bad_email_risk',
              accounts_status_code: response.status,
            })
            fields.email.msg = ' '
            fields.email.valid = false
            this.showError('showEmailNotFoundError', { fields })
          } else if (response.data.message === 'EMAIL_BOX_NOT_REACHABLE') {
            lurker.viewMessageEvt('error', {
              accounts_reason: 'email_box_not_reachable',
              accounts_status_code: response.status,
            })
            fields.email.msg = ' '
            fields.email.valid = false
            this.showError('showEmailNotFoundError', { fields })
          } else if (response.data.message === 'PROCESS_NOT_DONE_YET') {
            lurker.viewMessageEvt('error', {
              accounts_reason: 'pending_validation',
              accounts_status_code: response.status,
            })
            fields.email.msg = 'Ainda estamos procurando o seu email.'
            fields.email.valid = false
          } else if (this.invalidRecaptchaToken(data)) {
            lurker.viewMessageEvt('error', {
              accounts_reason: 'register_recaptcha_invalid_token',
              accounts_status_code: response.status,
            })
            this.showError('showGenericError')
          }
        } else {
          lurker.viewMessageEvt('error', {
            accounts_reason: 'forbidden_without_reason',
            accounts_status_code: response.status,
          })
          this.showError('showGenericError')
        }
      } else if (response.status === 400) {
        if (response.data && response.data.trim() === 'Missing recaptcha response token') {
          lurker.viewMessageEvt('error', {
            accounts_reason: 'register_recaptcha_missing_token',
            accounts_status_code: response.status,
          })
          this.showError('showGenericError')
        }
      } else if (response.status) {
        lurker.viewMessageEvt('error', {
          accounts_reason: 'unexpected_status_code',
          accounts_status_code: response.status,
        })
        this.showError('showGenericError')
      } else if (data === maxRetriesReachedError) {
        lurker.viewMessageEvt('error', {
          accounts_reason: 'reached_max_retries',
        })
        this.showError('showGenericError')
      } else {
        lurker.viewMessageEvt('error', {
          accounts_reason: 'unexpected_error',
          accounts_error_message: (data && data.message) || 'unknown',
        })
        this.showError('showGenericError')
      }

      this.resetRecaptcha()
      behaviors.scrollToTop()
    } finally {
      this.setState({ isSubmitting: false })
    }
  }

  resetRecaptcha() {
    if (this.recaptcha) {
      this.recaptcha.reset()
    }
  }

  showEmailAlreadyExistsError = () => {
    const { fields } = this.state
    fields.email.msg = emailAlreadyExists(this.props.onForgotPassword)
    fields.email.valid = false
    this.setState({ fields })
  }

  showDocumentAlreadyExistsError = msg => {
    const { fields } = this.state
    fields.legalDocument.msg = msg
    fields.legalDocument.valid = false
    this.setState({ fields })
  }

  handleSubmit = async event => {
    event.preventDefault()
    const { fields, isMEI, fullName, birthDate } = this.state
    const docType = fields.userType.value.toUpperCase()

    lurker.clickEvt('email', {
      doc_type: docType,
      is_mei: isMEI,
    })

    await this.validateBeforeSend()

    this.setState({ ...errors, isSubmitting: true })

    const { password } = fields
    try {
      if (fields.userType.value === 'cpf') {
        if (!fullName.valid || !birthDate.valid) {
          this.setState({ isSubmitting: false })

          if (!birthDate.valid) {
            lurker.viewMessageEvt('error', {
              accounts_reason: 'invalid_birth_date',
            })

            this.setState({
              birthDate: {
                ...birthDate,
                pristine: false,
              },
            })
          }

          if (!fullName.valid) {
            lurker.viewMessageEvt('error', {
              accounts_reason: 'invalid_complete_name',
            })

            this.setState({
              fullName: {
                ...fullName,
                pristine: false,
              },
            })
          }

          throw 'invalid fullName or invalid birthDate'
        }
      }

      await this.checkFormIsValid()
      await this.doRegistrationStack()
    } catch (error) {
      lurker.viewMessageEvt('error', {
        accounts_reason: 'invalid_fields',
        ...this.getInvalidFields(),
        ...getPasswordExtraData('invalid_password_reason', password),
      })
    }
  }

  onMailCheckSuggestionClick = suggestion => {
    lurker.viewMessageEvt('mailcheck_suggestion_accepted', {
      mailcheck_original: this.state.fields.email.value,
      mailcheck_suggestion: suggestion,
    })
    const valid = isValidEmail(suggestion)
    this.updateField('email', suggestion, valid, true)
    this.updateFocus('password')
  }

  renderEmail() {
    const { fields, isSubmitting } = this.state
    const { email } = fields
    const emailHasError = !email.valid && !email.pristine

    return (
      <RowWrapper>
        <Col size="100">
          <MailCheckInput
            label="E-mail"
            caption="Enviaremos um e-mail de confirmação."
            value={email.value}
            onChange={event => this.onEmailChange(event)}
            onBlur={event => this.onEmailBlur(event)}
            error={email.msg}
            hasError={emailHasError}
            disabled={isSubmitting}
            onSubmitEditing={() => this.updateFocus('password')}
            onSuggestionClick={this.onMailCheckSuggestionClick}
            inputRef={x => {
              this.inputs.email = x
            }}
          />
        </Col>
      </RowWrapper>
    )
  }

  renderPassword() {
    const { fields } = this.state
    const { password } = fields

    return (
      <StrongPasswordInput
        value={password.value}
        onChange={this.onPasswordChangeVariant}
        onBlur={this.onPasswordChangeVariant}
        maxLength={PASSWORD_MAX_LENGTH}
      />
    )
  }

  sendFacebookSignUpEventToGA = () => {
    if (window) {
      const { dataLayer } = window
      if (dataLayer) {
        dataLayer.push({ event: 'facebook_signup_success' })
      }
    }
  }

  onRecaptchaLoad = () => this.setState({ recaptchaReady: true })

  onRecaptchaVerify = recaptchaToken => {
    if (recaptchaToken) {
      this.updateField('recaptchaToken', recaptchaToken, true)
    } else {
      this.setState({ isSubmitting: false })
      this.recaptcha.reset()
      lurker.viewMessageEvt('error', {
        accounts_reason: 'recaptcha_failed_token',
      })
      this.showError('showGenericError')
    }
  }

  onRecaptchaError = e => {
    lurker.viewMessageEvt('error', {
      accounts_reason: 'recaptcha_error',
      accounts_error_message: (e && e.message) || 'unknown',
    })
  }

  onRecaptchaExpiration = () => this.updateField('recaptchaToken', '', false)

  isValidDocument = value => {
    const { userType } = this.state.fields
    return isValidDocument(value, userType.value)
  }

  getDocumentErrorMessage = () => {
    const { userType } = this.state.fields
    return userType.value === 'cpf'
      ? 'Campo obrigatório. Informe o número do seu CPF.'
      : 'Campo obrigatório. Informe o número do seu CNPJ.'
  }

  renderRecaptcha() {
    const { recaptchaToken } = this.state.fields
    const captchaHasError = !recaptchaToken.valid && !recaptchaToken.pristine

    return (
      <RecaptchaWrapper>
        <React.Fragment>
          <Recaptcha
            ref={e => {
              this.recaptcha = e
            }}
            sitekey={recaptchaSiteKey}
            onLoad={this.onRecaptchaLoad}
            onVerify={this.onRecaptchaVerify}
            onError={this.onRecaptchaError}
            onExpire={this.onRecaptchaExpiration}
          />
          {captchaHasError && (
            <CaptchaErrorMsgView>
              <ErrorMsg>Captcha não preenchido.</ErrorMsg>
            </CaptchaErrorMsgView>
          )}
        </React.Fragment>
      </RecaptchaWrapper>
    )
  }

  onMEIChange(isMEI) {
    lurker.clickEvt('mei_check_box_button')
    this.setState({
      isMEI,
    })
  }

  renderDocumentType() {
    const { legalDocument, userType } = this.state.fields
    const { isSubmitting, isMEI } = this.state

    return (
      <React.Fragment>
        <RowWrapper marginBottom="--spacing-1">
          <Col size="100">
            <span className="typo-body-medium font-semibold">Escolha o tipo da sua conta</span>
          </Col>
        </RowWrapper>

        <RowWrapper>
          <DocumentTypeSelection
            documentType={userType?.value}
            disabled={isSubmitting}
            onChange={this.onUserTypeChange}
          />
        </RowWrapper>

        <RowWrapper>
          <Col size="100">
            <DocumentInput
              value={legalDocument.value}
              state={!legalDocument.valid && !legalDocument.pristine && 'error'}
              stateMessage={legalDocument.msg || this.state.validators.legalDocument.message()}
              onChange={value => this.updateField('legalDocument', value)}
              onBlur={this.onLegalDocumentBlur}
              documentType={userType.value}
              disabled={isSubmitting}
              link={
                <ResponsiveTooltip
                  width={210}
                  text="
                      Pedimos este dado para garantir ainda mais segurança na sua conta. Não se
                      preocupe, a OLX não divulga dados pessoais."
                >
                  <AlertCircle />
                </ResponsiveTooltip>
              }
            />
          </Col>
        </RowWrapper>
        {userType.value === 'cnpj' && (
          <RowWrapper>
            <Col size="100">
              <Checkbox
                onClick={e => this.onMEIChange(e.target.checked)}
                aria-label="Checkbox para marcar se você é MEI"
                label="Sou MEI"
                checked={isMEI}
              />
            </Col>
          </RowWrapper>
        )}
      </React.Fragment>
    )
  }

  renderNickname() {
    const { isSubmitting, abTests, fields } = this.state
    const { nickname } = fields
    const nicknameHasError = !nickname.valid && !nickname.pristine
    const nicknameErrorMessage = nickname.msg
    return (
      <RowWrapper>
        <Col size="100">
          <TextInput
            id="nickname"
            label="Como você quer ser chamado(a)?"
            placeholder="Exemplo: João S."
            caption="Aparecerá em seu perfil, anúncios e chats."
            value={nickname.value}
            maxLength={100}
            onChange={this.onNicknameChange}
            onBlur={this.onNicknameBlur}
            disabled={isSubmitting}
            state={nicknameHasError && 'error'}
            stateMessage={nicknameErrorMessage}
            abTests={abTests}
          />
        </Col>
      </RowWrapper>
    )
  }

  onFullNameBlur = event => {
    const { value } = event.target

    const valid = value ? isValidFullName(value) : false

    this.setState({
      fullName: {
        value,
        valid,
        pristine: false,
      },
    })
  }

  onFullnameChange = event => {
    const { value } = event.target

    this.setState({
      fullName: {
        value,
        valid: true,
        pristine: true,
      },
    })
  }

  renderFullName = () => {
    const { fullName } = this.state

    const fullNameHasError = !fullName.valid && !fullName.pristine

    return (
      <RowWrapper>
        <Col size="100">
          <TextInput
            id="input-fullName"
            label="Nome completo"
            onBlur={this.onFullNameBlur}
            onChange={this.onFullnameChange}
            value={fullName.value}
            maxLength={100}
            state={fullNameHasError && 'error'}
            stateMessage={fullNameHasError && 'Por favor, preencha um nome válido'}
          />
        </Col>
      </RowWrapper>
    )
  }

  onBirthDateBlur = event => {
    const { value } = event.target
    const valid = value ? isValidateDate(value) : false

    this.setState({
      birthDate: {
        value,
        valid,
        pristine: false,
      },
    })
  }

  onBirthDateChange = event => {
    const { value } = event.target

    this.setState({
      birthDate: {
        value,
        valid: true,
        pristine: true,
      },
    })
  }

  renderDateBirth() {
    const { birthDate } = this.state

    const birthDateHasError = !birthDate.valid && !birthDate.pristine

    return (
      <RowWrapper>
        <Col size="100">
          <TextInputDate
            type="date"
            id="input-birthDate"
            label="Data de nascimento"
            onBlur={this.onBirthDateBlur}
            onChange={this.onBirthDateChange}
            placeholder="  /  /    "
            value={birthDate.value}
            maxLength={10}
            state={birthDateHasError && 'error'}
            stateMessage={birthDateHasError && 'Data de nascimento inválida'}
          />
        </Col>
      </RowWrapper>
    )
  }

  render() {
    const {
      facebookAppId,
      onRequireEmail,
      onPhoneNotValidatedError,
      onAccountSocialLoginTyping,
      onAccountCompleteData,
    } = this.props
    const {
      showGenericError,
      showInactiveError,
      showNicknameBlacklistError,
      showEmailBlacklistError,
      showEmailNotFoundError,
      showGoogleInitializationError,
      showPasswordlessError,
      isSubmitting,
      passwordlessEmail,
      fields,
      abTests,
    } = this.state

    return (
      <Content>
        <OauthLoginCallout
          facebookAppId={facebookAppId}
          onCancel={this.onCancel}
          onError={this.onOauthError}
          onRequireEmail={onRequireEmail}
          onSubmit={this.onOauthSubmit}
          onUserLoggedIn={this.onOauthSuccess}
          onFacebookLoginCallback={this.sendFacebookSignUpEventToGA}
          lurker={lurker}
          tracker={tracker}
          onPhoneNotValidatedError={onPhoneNotValidatedError}
          onAccountSocialLoginTyping={onAccountSocialLoginTyping}
          onAccountCompleteData={onAccountCompleteData}
          abTests={abTests}
          hideDivider
        />
        <AlternativeRuler />
        {showGenericError && (
          <ErrorMsgWrapper>
            <GenericErrorBox text="Não foi possível criar sua conta. Por favor, tente novamente." />
          </ErrorMsgWrapper>
        )}
        {showGoogleInitializationError && (
          <ErrorMsgWrapper>
            <GenericErrorBox
              showGoogleInitializationError
              text="Não foi possível entrar com o Google."
            />
          </ErrorMsgWrapper>
        )}
        {showNicknameBlacklistError && (
          <ErrorMsgWrapper>
            <GenericErrorBox text="Este apelido não pode ser utilizado." />
          </ErrorMsgWrapper>
        )}
        {showEmailBlacklistError && (
          <ErrorMsgWrapper>
            <GenericErrorBox text="Este e-mail não pode ser utilizado." />
          </ErrorMsgWrapper>
        )}
        {showEmailNotFoundError && (
          <ErrorMsgWrapper>
            <GenericErrorBox showEmailNotFoundError text="Este e-mail não existe." />
          </ErrorMsgWrapper>
        )}
        {showInactiveError && (
          <ErrorMsgWrapper>
            <GenericErrorBox
              showContactUs
              contactUsLink="https://ajuda.olx.com.br/s/article/reativar-conta-na-olx"
              text="Este e-mail não pode mais ser usado"
            />
          </ErrorMsgWrapper>
        )}
        {showPasswordlessError && (
          <ErrorMsgWrapper>
            <GenericErrorBox
              showContactUs={false}
              text={`Enviamos um e-mail ${passwordlessEmail ? `para ${passwordlessEmail}` : ''}
              para ajudar a acessar sua conta rapidamente.`}
            />
          </ErrorMsgWrapper>
        )}
        <PageForm method="POST" onSubmit={this.handleSubmit}>
          <RowWrapper>
            <Col size="100">
              <TextCenter>
                <span className="typo-body-medium">
                  Nos informe alguns dados para que possamos melhorar a sua experiência na OLX.
                </span>
              </TextCenter>
            </Col>
          </RowWrapper>

          {this.renderDocumentType()}

          {fields?.userType?.value === 'cpf' && this.renderFullName()}

          {this.renderNickname()}

          {fields?.userType?.value === 'cpf' && this.renderDateBirth()}

          {this.renderEmail()}

          {this.renderPassword()}

          {this.renderRecaptcha()}
          <ButtonWrapper>
            <LoadingButton
              className="w-full"
              size="medium"
              disabled={isSubmitting}
              isLoading={isSubmitting}
              onClick={this.handleSubmit}
            >
              Cadastre-se
            </LoadingButton>
          </ButtonWrapper>
        </PageForm>
      </Content>
    )
  }
}

RegisterTemplate.defaultProps = {
  maxRetries: 10,
}

RegisterTemplate.propTypes = {
  onRegisterSuccess: PropTypes.func,
  onRequireEmail: PropTypes.func,
  onForgotPassword: PropTypes.func,
  facebookAppId: PropTypes.string,
  maxRetries: PropTypes.number,
  onError: PropTypes.func,
  abTests: PropTypes.string,
  emailExistTest: PropTypes.bool,
  onPhoneNotValidatedError: PropTypes.func,
  onAccountSocialLoginTyping: PropTypes.func,
  onAccountCompleteData: PropTypes.func,
}

export default RegisterTemplate
